import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusLevumiItemNumberDictation } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemNumberDictation.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import '@chancenwerk/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { takeUntil } from 'rxjs';
import '../campus-component-text-to-speech-button.js';

@customElement('campus-dialog-edit-levumi-number-dictation-item')
export class CampusDialogEditLevumiNumberDictationItem extends ChromiumDialog<void> {
  static override get element_name() {
    return 'campus-dialog-edit-levumi-number-dictation-item';
  }

  @state() public levumiNumberDictationItem = new RXController(this, new CampusLevumiItemNumberDictation({}));

  @state() private readonly _levumiLevels = new RXController(
    this,
    LearningContentAPI.levumi.subscribeLevelsBySubject(LevumiSubject.NUMBER_DICTATION)(CampusLevumiLevel.allFields),
  );

  private readonly _form = new FormController(this, this.levumiNumberDictationItem.observable, {
    success: async dictationItem => this._handleFormSuccess(dictationItem),
  });

  override connectedCallback() {
    super.connectedCallback();
    this.levumiNumberDictationItem.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(
        e => (this.heading = !e._id ? 'Levumi Zahlendiktat-Item erstellen' : 'Levumi Zahlendiktat-Item bearbeiten'),
      );
  }

  @state() private _preview: Maybe<number> = null;

  private async _handleFormSuccess(levumiNumberDictation: CampusLevumiItemNumberDictation) {
    await ChromiumDialog.guard(async () => {
      // Possible switch of level reactivity fix
      const newLevel = this._levumiLevels.value?.find(l => l._id === levumiNumberDictation.levelID);
      if (newLevel) {
        this.levumiNumberDictationItem.value.level = newLevel;
      }

      if (!levumiNumberDictation._id) await levumiNumberDictation.create();
      else await levumiNumberDictation.save();

      void ChromiumSnackbar.show('Item gespeichert');
      await this.close();
    });
  }
  override content() {
    return html`
      <cr-flex space="md" padding="md">
        <cr-flex horizontal space="md" crossaxis-end>
          <cr-dropdown-selection
            hide-add-button
            placeholder="Level"
            ${this._form.field('levelID')}
            .selectables=${(this._levumiLevels.value ?? [])
              .sort((a, b) => (a.level.trim().toUpperCase() < b.level.trim().toUpperCase() ? -1 : 1))
              .map(l => l._id)}
            .renderString=${(levelID: string) => this._levumiLevels.value?.find(lev => lev._id === levelID)?.level}
            .renderItem=${(levelID: string) => {
              const level = this._levumiLevels.value?.find(lev => lev._id === levelID);
              if (!level || level.level === 'unspecified') return;
              return html`
                <cr-flex
                  center
                  crossaxis-center
                  horizontal
                  space="md"
                  padding="md"
                  ${css({ borderTop: `1px solid #f2f2f2`, cursor: 'pointer' })}
                >
                  <cr-text>${level.level}</cr-text>
                  <cr-button
                    mode="text"
                    icon="delete"
                    tone="error"
                    @click=${async e => {
                      e.stopPropagation();
                      await this._deleteLevel(level);
                    }}
                  >
                    Löschen
                  </cr-button>
                </cr-flex>
              `;
            }}
            flex
          ></cr-dropdown-selection>

          <cr-button
            icon="add"
            @click=${async e => {
              const levelName = await ChromiumDialog.prompt('Name für das Level eintragen', {
                placeholder: 'Level',
              });
              if (!levelName && this._levumiLevels.value?.some(val => val.level === levelName)) return;
              await new CampusLevumiLevel({
                level: levelName,
                subject: LevumiSubject.MATH,
              }).create();
              void ChromiumSnackbar.show(levelName + ' wurde für Mathe gespeichert');
            }}
          >
          </cr-button>
        </cr-flex>
        <cr-dropdown-selection
          placeholder="Kategorie"
          ${this._form.field('category')}
          .selectables=${[1, 2, 3, 4, 5]}
          flex
        ></cr-dropdown-selection>
        <cr-flex horizontal space="md">
          <cr-input
            flex
            type="number"
            ${this._form.field('item')}
            placeholder="Die Zahl die abgefragt wird *"
            @value-changed=${(e: ValueEvent<Maybe<number>>) => {
              if (this._preview !== e.detail.value) {
                this._preview = e.detail.value;
                this.levumiNumberDictationItem.value.item = e.detail.value;
              }
            }}
            .value=${this.levumiNumberDictationItem.value.item}
          ></cr-input>
          <campus-component-text-to-speech-button .text=${this._preview ?? this.levumiNumberDictationItem.value.item}>
            <cr-tooltip>
              <cr-text> Vorschau </cr-text>
            </cr-tooltip>
          </campus-component-text-to-speech-button>
        </cr-flex>

        <cr-flex horizontal crossaxis-center end space="md">
          ${this.levumiNumberDictationItem.value._id
            ? html`
                <campus-delete-button
                  @click=${async () => {
                    if (
                      !(await ChromiumDialog.confirm('Levumi Zahlendiktat-Item wirklich löschen?', {
                        highlightDecline: true,
                      }))
                    )
                      return;
                    await this.levumiNumberDictationItem.value.remove();
                    await this.close();
                  }}
                >
                </campus-delete-button>
              `
            : nothing}
          <cr-button icon="save" @click=${() => this._form.validate()}>
            ${!this.levumiNumberDictationItem.value._id ? html` Erstellen ` : html` Speichern `}
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private async _deleteLevel(level: CampusLevumiLevel) {
    const levelIsUsed = await LearningContentAPI.levumi.getLevelCurrentlyInUse(level._id)();
    if (levelIsUsed) {
      if (
        await ChromiumDialog.confirm(
          html`
            <cr-flex center padding="md" space="md" space-between crossaxis-center>
              <cr-h2 ${css({ color: 'red' })}>Achtung!</cr-h2>
              <cr-h3>
                Dieses Level wird im Augenblick noch von anderen Aufgaben benutzt. Bei der Löschung werden diese
                Aufgaben zu dem Level 'unspecified' hinzugefügt. Sie sind dann nicht mehr in den jeweiligen Aufgaben
                verfügbar und müssen manuell zu einer Station hinzugefügt werden.
              </cr-h3>
            </cr-flex>
          `,
          { highlightDecline: true },
        )
      ) {
        const sameLevelAsCurrentItem = this.levumiNumberDictationItem.value.level._id === level._id;
        await LearningContentAPI.levumi.changeLevelToUnspecified(CampusLevumiItemNumberDictation._class, level._id)();
        await level.remove();
        if (sameLevelAsCurrentItem) await this.close();
      }
    } else {
      await level.remove();
    }
  }

  public override async cancel() {
    this.levumiNumberDictationItem.value.revert();

    await super.cancel();
  }
}
